import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Select from '@/components/ui/Select';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import {
  calculateVacationDaysByYear,
  sumVacationDays,
  normalizeRemainingVacationDays,
} from '../utils/vacationDays';
import {
  clampDateToRange,
  formatIsoDate,
  getAbsenceDateLimits,
  getLowerBoundDate,
  getUpperBoundDate,
  parseDateValue,
} from '../utils/dateLimits';

const DEFAULT_FORM = {
  employeeId: '',
  type: 'vacation',
  startDate: '',
  endDate: '',
  dayPart: 'full-day',
  reason: '',
};

const TYPE_OPTION_VALUES = ['vacation', 'sick', 'personal', 'emergency'];

const MIN_REASON_LENGTH = 5;

const ManagerCreateAbsenceModal = ({
  isOpen,
  onClose,
  onSubmit,
  employees = [],
  currentUserEmployee = null,
  isSubmitting = false,
}) => {
  const { t } = useTranslation(['timeOffManagement', 'common']);
  const translate = useCallback(
    (key, fallback) => t(`timeOffManagement:${key}`, fallback),
    [t],
  );

  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [error, setError] = useState('');
  const { minDate, maxDate, minDateString, maxDateString } = useMemo(getAbsenceDateLimits, []);

  const formatDays = useCallback(
    (value) => {
      const numericValue = Number(value);
      const rounded = Number.isFinite(numericValue)
        ? Math.round(numericValue * 100) / 100
        : 0;
      const displayValue = Number.isInteger(rounded)
        ? rounded
        : Number(rounded.toFixed(2));
      const template = translate(
        `duration.days.${Math.abs(rounded) === 1 ? 'singular' : 'plural'}`,
        Math.abs(rounded) === 1 ? '{{count}} day' : '{{count}} days',
      );
      return formatTranslation(template, { count: displayValue });
    },
    [translate],
  );

  const typeOptions = useMemo(
    () =>
      TYPE_OPTION_VALUES.map((value) => ({
        value,
        label: translate(
          `types.${value}`,
          value.charAt(0).toUpperCase() + value.slice(1),
        ),
      })),
    [translate],
  );

  const employeeOptions = useMemo(() => {
    return (employees ?? [])
      .filter((employee) => employee && (employee.id || employee.employeeId))
      .map((employee) => {
        const id = String(employee.id ?? employee.employeeId);
        return {
          label:
            employee?.name
            ?? employee?.fullName
            ?? translate('defaults.positionFallback', 'Team Member'),
          value: id,
          record: employee,
        };
      });
  }, [employees, translate]);

  const defaultEmployeeId = useMemo(() => {
    if (currentUserEmployee?.id) {
      return String(currentUserEmployee.id);
    }
    return employeeOptions[0]?.value ?? '';
  }, [currentUserEmployee?.id, employeeOptions]);

  const selectedEmployee = useMemo(() => {
    if (!formValues.employeeId) {
      return null;
    }
    const match = employeeOptions.find((option) => option.value === formValues.employeeId);
    if (match?.record) {
      return match.record;
    }
    return (employees ?? []).find((employee) => {
      const candidateId = String(employee?.id ?? employee?.employeeId ?? '');
      return candidateId === formValues.employeeId;
    }) ?? null;
  }, [employeeOptions, employees, formValues.employeeId]);

  useEffect(() => {
    if (!isOpen) {
      setFormValues(DEFAULT_FORM);
      setError('');
      return;
    }

    setFormValues((prev) => ({
      ...DEFAULT_FORM,
      employeeId: defaultEmployeeId,
    }));
    setError('');
  }, [defaultEmployeeId, isOpen]);

  const calendarDuration = useMemo(() => {
    if (!formValues.startDate || !formValues.endDate) {
      return 0;
    }
    const start = new Date(formValues.startDate);
    const end = new Date(formValues.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return 0;
    }
    const diffMs = end.getTime() - start.getTime();
    return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
  }, [formValues.endDate, formValues.startDate]);

  const normalizedRemainingVacation = useMemo(() => {
    const primary = normalizeRemainingVacationDays(
      selectedEmployee?.remainingVacationDaysPerYear,
    );

    const rawFallback = normalizeRemainingVacationDays(
      selectedEmployee?.raw?.remainingVacationDaysPerYear,
    );

    if (Object.keys(rawFallback).length === 0) {
      return primary;
    }

    return { ...rawFallback, ...primary };
  }, [
    selectedEmployee?.raw?.remainingVacationDaysPerYear,
    selectedEmployee?.remainingVacationDaysPerYear,
  ]);

  const fallbackVacationAllocation = useMemo(() => {
    const candidate = selectedEmployee?.vacationDaysPerYear;
    const numericCandidate =
      typeof candidate === 'number'
        ? candidate
        : typeof candidate === 'string'
          ? Number(candidate)
          : null;
    if (typeof numericCandidate === 'number' && Number.isFinite(numericCandidate) && numericCandidate >= 0) {
      return Math.round(numericCandidate * 100) / 100;
    }
    return null;
  }, [selectedEmployee?.vacationDaysPerYear]);

  const hireDate = useMemo(() => {
    const candidates = [
      selectedEmployee?.raw?.hireDate,
      selectedEmployee?.raw?.startDate,
      selectedEmployee?.hireDate,
    ];
    for (const candidate of candidates) {
      if (!candidate || candidate === '—') {
        continue;
      }
      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return null;
  }, [selectedEmployee]);

  const vacationBreakdown = useMemo(
    () =>
      formValues.type === 'vacation'
        ? calculateVacationDaysByYear(formValues.startDate, formValues.endDate, 'full-day')
        : {},
    [formValues.endDate, formValues.startDate, formValues.type],
  );

  const resolvedRemainingVacation = useMemo(() => {
    if (typeof fallbackVacationAllocation !== 'number' || fallbackVacationAllocation < 0) {
      return normalizedRemainingVacation;
    }

    const requestedYears = Object.keys(vacationBreakdown);
    if (requestedYears.length === 0) {
      return normalizedRemainingVacation;
    }

    const hireYear = hireDate?.getFullYear() ?? null;

    const getDaysInYear = (year) => {
      const start = Date.UTC(year, 0, 1);
      const end = Date.UTC(year + 1, 0, 1);
      return Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)));
    };

    const calculateDefaultBalance = (year) => {
      if (!Number.isFinite(year)) {
        return null;
      }
      if (!hireDate || hireYear !== year) {
        return fallbackVacationAllocation;
      }

      const hireUtc = Date.UTC(year, hireDate.getMonth(), hireDate.getDate());
      const endOfYearUtc = Date.UTC(year, 11, 31);
      if (hireUtc > endOfYearUtc) {
        return 0;
      }

      const remainingDays = Math.max(
        0,
        Math.floor((endOfYearUtc - hireUtc) / (24 * 60 * 60 * 1000)) + 1,
      );
      const totalDays = getDaysInYear(year);
      if (remainingDays <= 0 || totalDays <= 0) {
        return 0;
      }

      const prorated = Math.ceil((remainingDays / totalDays) * fallbackVacationAllocation);
      return Math.min(fallbackVacationAllocation, Math.max(0, prorated));
    };

    let mutated = false;
    const merged = { ...normalizedRemainingVacation };

    requestedYears.forEach((yearKey) => {
      if (merged[yearKey] !== undefined) {
        return;
      }
      const yearNumber = Number.parseInt(yearKey, 10);
      const defaultValue = calculateDefaultBalance(yearNumber);
      if (typeof defaultValue === 'number' && defaultValue >= 0) {
        merged[yearKey] = Math.round(defaultValue * 100) / 100;
        mutated = true;
      }
    });

    return mutated ? merged : normalizedRemainingVacation;
  }, [fallbackVacationAllocation, hireDate, normalizedRemainingVacation, vacationBreakdown]);

  const vacationDuration = useMemo(
    () => (formValues.type === 'vacation' ? sumVacationDays(vacationBreakdown) : 0),
    [formValues.type, vacationBreakdown],
  );

  const vacationValidation = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return { isValid: true, breakdown: {}, totalDays: 0 };
    }

    const totalDays = vacationDuration;

    if (!Number.isFinite(totalDays) || totalDays <= 0) {
      return {
        isValid: false,
        reason: 'no-weekdays',
        breakdown: vacationBreakdown,
        totalDays: 0,
      };
    }

    for (const [year, value] of Object.entries(vacationBreakdown)) {
      const requested = Number(value);
      if (!(requested > 0)) {
        continue;
      }

      const availableRaw =
        resolvedRemainingVacation[year] ??
        (typeof fallbackVacationAllocation === 'number'
          ? fallbackVacationAllocation
          : undefined);

      const normalizedAvailable =
        typeof availableRaw === 'number' && availableRaw >= 0
          ? availableRaw
          : typeof fallbackVacationAllocation === 'number' && fallbackVacationAllocation >= 0
            ? fallbackVacationAllocation
            : 0;

      if (requested - normalizedAvailable > 0.0001) {
        return {
          isValid: false,
          reason: 'insufficient',
          breakdown: vacationBreakdown,
          totalDays,
          year,
          available: normalizedAvailable,
          requested,
        };
      }
    }

    return { isValid: true, breakdown: vacationBreakdown, totalDays };
  }, [
    fallbackVacationAllocation,
    formValues.type,
    normalizedRemainingVacation,
    vacationBreakdown,
    vacationDuration,
  ]);

  const requestedDuration =
    formValues.type === 'vacation'
      ? vacationValidation.totalDays ?? 0
      : calendarDuration;

  const vacationBreakdownLabel = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return '';
    }

    const entries = Object.entries(vacationBreakdown)
      .filter(([, value]) => Number.isFinite(Number(value)) && Number(value) > 0)
      .sort(
        ([firstYear], [secondYear]) => Number.parseInt(firstYear, 10) - Number.parseInt(secondYear, 10),
      )
      .map(([year, value]) =>
        formatTranslation(
          translate('modal.managerCreate.vacationBalance.entry', '{{year}}: {{days}}'),
          { year, days: formatDays(value) },
        ),
      );

    return entries.join(', ');
  }, [formValues.type, formatDays, translate, vacationBreakdown]);

  const vacationRestrictionMessage = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return '';
    }
    if (vacationValidation.isValid) {
      return '';
    }

    switch (vacationValidation.reason) {
      case 'no-weekdays':
        return translate(
          'modal.managerCreate.errors.vacationWeekday',
          'Vacation requests must include at least one weekday.',
        );
      case 'insufficient':
        return formatTranslation(
          translate(
            'modal.managerCreate.errors.vacationInsufficient',
            'Only {{days}} remain for {{year}}.',
          ),
          {
            days: formatDays(vacationValidation.available ?? 0),
            year: vacationValidation.year,
          },
        );
      default:
        return translate(
          'modal.managerCreate.errors.vacationExceeds',
          'This vacation request exceeds the remaining balance.',
        );
    }
  }, [formValues.type, formatDays, translate, vacationValidation]);

  const vacationBalanceDisplay = useMemo(() => {
    const entries = Object.entries(resolvedRemainingVacation).sort(
      ([firstYear], [secondYear]) => Number.parseInt(firstYear, 10) - Number.parseInt(secondYear, 10),
    );

    if (entries.length > 0) {
      return entries
        .map(([year, value]) =>
          formatTranslation(
            translate('modal.managerCreate.vacationBalance.entry', '{{year}}: {{days}}'),
            { year, days: formatDays(value) },
          ),
        )
        .join(' • ');
    }

    if (typeof fallbackVacationAllocation === 'number') {
      return formatTranslation(
        translate('modal.managerCreate.duration.perYear', '{{days}} per year'),
        { days: formatDays(fallbackVacationAllocation) },
      );
    }

    return null;
  }, [
    fallbackVacationAllocation,
    formatDays,
    resolvedRemainingVacation,
    translate,
  ]);

  const displayDurationLabel =
    requestedDuration > 0
      ? formatDays(requestedDuration)
      : translate('modal.managerCreate.duration.pending', 'Duration pending');

  const startMaxDateString = useMemo(
    () => formatIsoDate(getUpperBoundDate(formValues.endDate, maxDate)),
    [formValues.endDate, maxDate],
  );

  const endMinDateString = useMemo(
    () => formatIsoDate(getLowerBoundDate(formValues.startDate, minDate)),
    [formValues.startDate, minDate],
  );

  if (!isOpen) {
    return null;
  }

  const handleStartDateChange = (value) => {
    setFormValues((prev) => {
      const boundedStart = clampDateToRange(value, minDate, maxDate);
      const next = { ...prev, startDate: boundedStart };
      const currentEnd = clampDateToRange(prev.endDate, minDate, maxDate);

      const startDateValue = parseDateValue(boundedStart);
      const endDateValue = parseDateValue(currentEnd);

      if (!currentEnd) {
        next.endDate = boundedStart;
      } else if (startDateValue && endDateValue && endDateValue < startDateValue) {
        next.endDate = boundedStart;
      } else {
        next.endDate = currentEnd;
      }
      return next;
    });
  };

  const handleEndDateChange = (value) => {
    setFormValues((prev) => {
      const next = { ...prev };
      const boundedEnd = clampDateToRange(value, minDate, maxDate);
      const boundedStart = clampDateToRange(prev.startDate, minDate, maxDate);
      const startDateValue = parseDateValue(boundedStart);
      const endDateValue = parseDateValue(boundedEnd);

      next.startDate = boundedStart;

      if (!boundedEnd) {
        next.endDate = '';
      } else if (startDateValue && endDateValue && endDateValue < startDateValue) {
        next.endDate = boundedStart;
      } else {
        next.endDate = boundedEnd;
      }

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    setError('');

    const trimmedReason = (formValues.reason ?? '').trim();
    const reasonIsRequired = formValues.type === 'emergency' || formValues.type === 'personal';
    const isEmergencyRequest = formValues.type === 'emergency';

    if (!formValues.employeeId) {
      setError(
        translate('modal.managerCreate.errors.selectEmployee', 'Select an employee to create this absence.'),
      );
      return;
    }

    if (!formValues.startDate || !formValues.endDate) {
      setError(
        translate('modal.managerCreate.errors.missingDates', 'Please choose a start and end date.'),
      );
      return;
    }

    if (new Date(formValues.endDate) < new Date(formValues.startDate)) {
      setError(
        translate(
          'modal.managerCreate.errors.endBeforeStart',
          'End date cannot be earlier than the start date.',
        ),
      );
      return;
    }

    if (reasonIsRequired && trimmedReason.length < MIN_REASON_LENGTH) {
      setError(
        translate(
          'modal.managerCreate.errors.reasonRequired',
          'Provide a short note for the record (at least 5 characters).',
        ),
      );
      return;
    }

    if (formValues.type === 'vacation' && !vacationValidation.isValid) {
      setError(
        vacationRestrictionMessage
          || translate(
            'modal.managerCreate.errors.vacationExceeds',
            'This vacation request exceeds the remaining balance.',
          ),
      );
      return;
    }

    if (requestedDuration <= 0) {
      setError(
        translate(
          'modal.managerCreate.errors.invalidSelection',
          'Please choose valid dates for this absence.',
        ),
      );
      return;
    }

    try {
      const submittedReason =
        trimmedReason.length > 0
          ? trimmedReason
          : isEmergencyRequest
            ? translate('defaults.emergencyReason', 'Emergency absence request')
            : '';

      await onSubmit({
        ...formValues,
        employeeId: formValues.employeeId,
        reason: submittedReason,
        duration: requestedDuration,
        dayPart: 'full-day',
      });
      onClose?.();
    } catch (submissionError) {
      const message =
        submissionError?.response?.data?.message
        ?? submissionError?.message
        ?? translate(
          'modal.managerCreate.errors.submitFailed',
          'Failed to create the absence. Please try again.',
        );
      setError(message);
    }
  };

  const trimmedReason = (formValues.reason ?? '').trim();
  const reasonIsRequired = formValues.type === 'emergency' || formValues.type === 'personal';
  const isReasonOptional = !reasonIsRequired;
  const isEmergencyRequest = formValues.type === 'emergency';
  const disableSubmit =
    !formValues.employeeId
    || !formValues.startDate
    || !formValues.endDate
    || (reasonIsRequired && trimmedReason.length < MIN_REASON_LENGTH)
    || requestedDuration <= 0
    || (formValues.type === 'vacation' && !vacationValidation.isValid);

  const safeEmployeeName =
    selectedEmployee?.name
    ?? selectedEmployee?.fullName
    ?? translate('defaults.positionFallback', 'Team Member');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevation-4 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {translate('modal.managerCreate.title', 'Add Approved Absence')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate(
                'modal.managerCreate.subtitle',
                'Create an absence that is immediately approved and added to the team calendar.',
              )}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSubmitting}>
            <Icon name="X" size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
              <div className="space-y-4">
                <Select
                  label={translate('modal.managerCreate.fields.employee', 'Employee')}
                value={formValues.employeeId}
                onChange={(value) => setFormValues((prev) => ({ ...prev, employeeId: value }))}
                options={employeeOptions}
                required
                searchable
                placeholder={translate('modal.managerCreate.placeholders.employee', 'Select team member')}
              />

              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-sm font-semibold text-foreground">{safeEmployeeName}</p>
                {selectedEmployee?.role && (
                  <p className="text-xs text-muted-foreground">{selectedEmployee.role}</p>
                )}
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                  <div>
                    <p className="uppercase tracking-wide text-[11px] text-muted-foreground/80">
                      {translate('modal.managerCreate.fields.primaryLocation', 'Primary location')}
                    </p>
                    <p className="font-medium text-foreground">
                      {selectedEmployee?.location || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wide text-[11px] text-muted-foreground/80">
                      {translate('modal.managerCreate.fields.vacationBalance', 'Vacation balance')}
                    </p>
                    <p className="font-medium text-foreground">
                      {vacationBalanceDisplay
                        ?? translate(
                          'modal.managerCreate.vacationBalance.fallback',
                          'Check with the team member',
                        )}
                    </p>
                  </div>
                </div>
              </div>

              <Select
                label={translate('modal.managerCreate.fields.type', 'Absence Type')}
                value={formValues.type}
                onChange={(value) => setFormValues((prev) => ({ ...prev, type: value }))}
                options={typeOptions}
                required
              />

              <DatePicker
                label={translate('modal.managerCreate.fields.startDate', 'Start Date')}
                value={formValues.startDate}
                onChange={(event) => handleStartDateChange(event?.target?.value ?? '')}
                min={minDateString}
                max={startMaxDateString}
                required
              />

              <DatePicker
                label={translate('modal.managerCreate.fields.endDate', 'End Date')}
                value={formValues.endDate}
                onChange={(event) => handleEndDateChange(event?.target?.value ?? '')}
                min={endMinDateString}
                max={maxDateString}
                required
              />

              <div className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2 text-foreground">
                  <Icon name="Clock" size={16} />
                  <span>{displayDurationLabel}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formValues.type === 'vacation'
                    ? translate(
                      'modal.managerCreate.duration.vacationInfo',
                      'Weekends are excluded from the requested vacation days.',
                    )
                    : translate(
                      'modal.managerCreate.duration.defaultInfo',
                      'Duration is calculated automatically based on the selected dates.',
                    )}
                </p>
                {formValues.type === 'vacation' && vacationBreakdownLabel && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatTranslation(
                      translate('modal.managerCreate.duration.breakdown', 'Breakdown: {{breakdown}}'),
                      { breakdown: vacationBreakdownLabel },
                    )}
                  </p>
                )}
                {formValues.type === 'vacation' && vacationRestrictionMessage && (
                  <p className="mt-2 text-xs text-destructive">{vacationRestrictionMessage}</p>
                )}
              </div>

              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1">
                    <span>{translate('modal.managerCreate.fields.reason', 'Reason')}</span>
                    {isReasonOptional ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        {translate(
                          'modal.managerCreate.reason.optionalLabel',
                          '(optional for vacation or sick)',
                        )}
                      </span>
                    ) : (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </label>
                  <textarea
                    className="min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={formValues.reason}
                    onChange={(event) =>
                      setFormValues((prev) => ({ ...prev, reason: event?.target?.value ?? '' }))
                    }
                    placeholder={isReasonOptional
                      ? translate(
                        'modal.managerCreate.placeholders.reasonDefault',
                        'Share the context for this approved absence...',
                      )
                      : translate(
                        'modal.managerCreate.placeholders.reasonRequired',
                        'Share the details needed for emergency or personal coverage...',
                      )}
                  />
                  <p className="text-xs text-muted-foreground">
                    {translate(
                      'modal.managerCreate.notes.storage',
                      'This note is stored with the absence record for future reference.',
                    )}
                  </p>
                </div>

                <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                  {translate(
                    'modal.managerCreate.notes.autoApproval',
                    'Absences created here skip the approval queue and notify the employee immediately.',
                  )}
                </div>

                {error && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-border px-6 py-4">
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {translate('actions.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              iconName="Check"
              loading={isSubmitting}
              disabled={disableSubmit || isSubmitting}
            >
              {translate('modal.managerCreate.actions.create', 'Create Absence')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManagerCreateAbsenceModal;
